import { AfterViewInit, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { File } from '@awesome-cordova-plugins/file/ngx';
import { Network } from '@capacitor/network';
import { SplashScreen } from '@capacitor/splash-screen';
import { KeepAwake } from '@capacitor-community/keep-awake';
import { FirebaseAnalytics } from '@capacitor-firebase/analytics';
import { SocialLogin } from '@capgo/capacitor-social-login';
import { ADJUST_EVENTS } from '@core/adjust/constants/adjust.constants';
import { AlertsComponent } from '@core/alerts/components/alerts/alerts.component';
import { AppConfigsService } from '@core/app-configs/services/app-configs.service';
import { AppFeaturesService } from '@core/app-features/services/app-features.service';
import { appInitializerFactory } from '@core/app-initializer/app-initializer.service';
import { AppTrackingService } from '@core/app-tracking/app-tracking.service';
import { migrateUnfinishedLocalMatches } from '@core/database/migrations/unfinished-games.db-migration';
import { EventOverlayComponent } from '@core/event-overlay/components/event-overlay/event-overlay.component';
import { IngameAnimationComponent } from '@core/ingame-animation/components/ingame-animation/ingame-animation.component';
import { HealthService } from '@core/monitoring/services/health.service';
import { OnlinePlayLobbyNavigationService } from '@core/online-play/services/online-play-lobby-navigation.service';
import { SplashOverlayComponent } from '@core/splash-overlay/splash-overlay.component';
import { SplashOverlayService } from '@core/splash-overlay/splash-overlay.service';
import { TextSizeService } from '@core/text-size/services/text-size.service';
import { DcThemeService } from '@dartcounter/ui';
import { Discount } from '@dc-core/dc-backend/dc-classes';
import { PreferenceService } from '@dc-core/dc-services/preference/preference.service';
import { IonApp, IonRouterOutlet, MenuController, NavController, Platform } from '@ionic/angular/standalone';
import { RouterEventsService } from '@services/router-events.service';
import { initializeApp } from 'firebase/app';
import { DeviceDetectorService } from 'ngx-device-detector';
import { exhaustMap, firstValueFrom, interval } from 'rxjs';
import { AudioService } from 'src/app/core/audio/services/audio.service';
import { NativeAudioService } from 'src/app/core/audio/services/native-audio.service';
import { LoggingService } from 'src/app/core/logging/services/logging.service';
import { ThemePreferenceDialogComponent } from 'src/app/modules/account-settings/dialogs/theme-preference/theme-preference.dialog';
import { CookieConsentService } from 'src/app/services/cookie-consent.service';
import { UpgradeService } from 'src/app/services/upgrade.service';
import { ModalService } from 'src/app/shared/services/modal.service';
import { slideDialogClasses } from 'src/app/shared/utils/dialog.utils';
import { environment, socials } from 'src/environments/environment';
import { register as registerSwiper } from 'swiper/element/bundle';

import { OneSignalService } from '../services/one-signal.service';
import { NATIVE_SPLASH_FADE_OUT_DURATION } from './app.globals';
import { AdjustService } from './core/adjust/services/adjust.service';
import { LocaleService } from './core/locale/locale.service';
import { TextSizePreferenceDialogComponent } from './modules/account-settings/dialogs/text-size-preference/text-size-preference.dialog';
import { InGameMenuComponent } from './modules/games/components/in-game-menu/in-game-menu.component';
import { PartyGameSplashScreenOverlayComponent } from './modules/games/party-games/components/party-game-splash-screen/party-game-splash-screen-overlay.component';
import { OnlineMaintenanceSchedulerService } from './modules/maintenance/services/online-maintenance-scheduler.service';
import { OmniSettingsMenuComponent } from './modules/smart-devices/omni/components/omni-settings-menu/omni-settings-menu.component';
import { VirtSettingsMenuComponent } from './modules/smart-devices/virt/omni-settings-menu/components/virt-settings-menu.component';
import { AppStateService } from './services/app-state.service';
import { JanusEventsListenerService } from './services/janus/janus-events-listener.service';
import { VenueDeeplinkTargetArcadeService } from './services/venues/venue-deeplink-target-arcade.service';
import { BadgeUnlockedOverlayComponent } from './shared/components/badge-unlocked-overlay/badge-unlocked-overlay.component';
import { DC_ROUTING, toAbsoluteRoute } from './shared/routing.tokens';
import { DeepLinkService } from './shared/services/deep-link.service';

registerSwiper();

@Component({
    selector: 'app-root',
    standalone: true,
    imports: [
        IonApp,
        SplashOverlayComponent,
        AlertsComponent,
        InGameMenuComponent,
        EventOverlayComponent,
        IngameAnimationComponent,
        OmniSettingsMenuComponent,
        VirtSettingsMenuComponent,
        IonRouterOutlet,
        BadgeUnlockedOverlayComponent,
        PartyGameSplashScreenOverlayComponent,
    ],
    templateUrl: 'app.component.html',
})
export class AppComponent implements OnInit, AfterViewInit {
    public lobbyNavigation = inject(OnlinePlayLobbyNavigationService);
    public preferenceService: PreferenceService = inject(PreferenceService);
    public themeService: DcThemeService = inject(DcThemeService); // Do not remove
    public loggingService: LoggingService = inject(LoggingService); // Do not remove
    public routerEventsService: RouterEventsService = inject(RouterEventsService); // Do not remove
    public platform: Platform = inject(Platform);

    private deviceDetectorService: DeviceDetectorService = inject(DeviceDetectorService);
    private cookieConsentService: CookieConsentService = inject(CookieConsentService);
    private appStateService: AppStateService = inject(AppStateService);
    private deepLinkService: DeepLinkService = inject(DeepLinkService);
    private menu: MenuController = inject(MenuController);
    private adjustService: AdjustService = inject(AdjustService);
    private file: File = inject(File);
    private nativeAudioService: NativeAudioService = inject(NativeAudioService);
    private navController: NavController = inject(NavController);
    private modalService: ModalService = inject(ModalService);
    private upgradeService: UpgradeService = inject(UpgradeService);
    private audioService: AudioService = inject(AudioService);
    private localeService: LocaleService = inject(LocaleService);
    private janusEventsListenerService: JanusEventsListenerService = inject(JanusEventsListenerService);
    private splashOverlayService: SplashOverlayService = inject(SplashOverlayService);
    private healthService: HealthService = inject(HealthService);
    private appFeaturesService: AppFeaturesService = inject(AppFeaturesService);
    private appConfigsService: AppConfigsService = inject(AppConfigsService);
    private onlineMaintenanceSchedulerService: OnlineMaintenanceSchedulerService = inject(
        OnlineMaintenanceSchedulerService
    );
    private appTrackingService: AppTrackingService = inject(AppTrackingService);
    private oneSignalService: OneSignalService = inject(OneSignalService);
    private textSizeService: TextSizeService = inject(TextSizeService);
    private venueTargetArcadeService: VenueDeeplinkTargetArcadeService = inject(VenueDeeplinkTargetArcadeService);
    private destroyRef: DestroyRef = inject(DestroyRef);

    private readonly appConfigurationRefreshIntervalMs = 15 * 60 * 1000; // 15 minutes

    public readonly isWeb = environment.isWeb;

    private readonly isAppOnly = (): boolean => !environment.isWeb && this.platform.is('capacitor');
    private readonly isAppOrMobile = (): boolean =>
        !environment.isWeb || this.deviceDetectorService.isMobile() || this.deviceDetectorService.isTablet();

    constructor() {
        this.bootstrap();
    }

    public async ngOnInit(): Promise<void> {
        this.onlineMaintenanceSchedulerService.init();
        this.textSizeService.initialize();

        this.initializeFirebase();
        if (!environment.local) void this.splashOverlayService.showAppSplashScreen(null);
        void this.runStartupInitialization();

        this.cookieConsentService.load();

        this.menu.enable(true, 'mainmenu');
        this.menu.enable(false, 'ingamemenu');
        this.menu.enable(false, 'omnisettingsmenu');
        this.menu.enable(false, 'virtsettingsmenu');

        this.localeService.registerLocales();

        if (this.isWeb) {
            document.documentElement.classList.add(this.isAppOrMobile() ? 'app-font-size' : 'web-font-size');
        } else {
            document.documentElement.classList.add('app-font-size');
        }

        this.upgradeService.checkDiscount('year').then((discount: Discount) => {
            this.upgradeService.currentActiveDiscount.set(discount);
        });

        this.janusEventsListenerService.initJanusEventsListener();

        if (environment.debug && !this.platform.is('capacitor')) {
            window.addEventListener('keydown', this.handleDevShortcuts);
        }

        this.platform.ready().then(async () => {
            this.controlSplashScreen();

            this.logAppStartedEvent();

            this.initAudioService();

            this.initTracking();
            void this.oneSignalService.initialize();
            this.initSocialLogins();

            try {
                KeepAwake.keepAwake();
            } catch {}

            const status = await Network.getStatus();
            this.appStateService.setLatestNetworkStatus(status);
            this.appStateService.startNetworkListener();
        });

        if (this.isAppOrMobile()) {
            this.appStateService.listenToStateChanges();
        }

        if (this.isAppOnly()) {
            this.registerIncomingDeepLinkHandlers();
            void this.deepLinkService.initDeepLinks();
        }
    }

    public ngAfterViewInit(): void {
        this.cookieConsentService.init();
    }

    private async bootstrap(): Promise<void> {
        await migrateUnfinishedLocalMatches();
    }

    private async runStartupInitialization(): Promise<void> {
        try {
            await firstValueFrom(
                appInitializerFactory(
                    this.healthService,
                    this.appFeaturesService,
                    this.appConfigsService,
                    this.navController
                )()
            );
        } finally {
            this.splashOverlayService.hideSplashOverlay();

            if (!this.destroyRef.destroyed) this.startPeriodicAppConfigurationRefresh();
        }
    }

    private startPeriodicAppConfigurationRefresh(): void {
        interval(this.appConfigurationRefreshIntervalMs)
            .pipe(
                exhaustMap(() =>
                    Promise.all([
                        this.appFeaturesService.registerEnabledAppFeatures(),
                        this.appConfigsService.registerAppConfigs(),
                    ])
                ),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();
    }

    private async controlSplashScreen(): Promise<void> {
        if (!this.platform.is('capacitor')) return;

        setTimeout(() => {
            this.hideNativeSplashScreen();
        }, 200);
    }

    private hideNativeSplashScreen(): void {
        SplashScreen.hide({ fadeOutDuration: NATIVE_SPLASH_FADE_OUT_DURATION }).finally(() => {
            this.appStateService.splashScreenIsHidden = true;
        });
    }

    private async initializeFirebase(): Promise<void> {
        if (this.platform.is('capacitor')) return;
        initializeApp(environment.firebase);
    }

    private initAudioService(): void {
        this.audioService.initialize(
            !this.platform.is('capacitor') ? 'web' : this.platform.is('android') ? 'android' : 'ios',
            this.file,
            this.nativeAudioService
        );
    }

    private async initTracking(): Promise<void> {
        await this.appTrackingService.initAppTracking(); // Before initializing Adjust
        this.adjustService.initAdjust();

        this.adjustService.trackEvent(ADJUST_EVENTS.FIRST_APP_STARTED);
    }

    private logAppStartedEvent(): void {
        FirebaseAnalytics.logEvent({ name: 'app_started', params: { version: environment.version } });
    }

    private initSocialLogins(): void {
        SocialLogin.initialize({
            apple: {
                clientId: socials.apple.clientId,
                redirectUrl: this.platform.is('ios') ? undefined : socials.apple.redirectUrl,
            },
            facebook: {
                appId: socials.facebook.clientId,
                clientToken: socials.facebook.clientToken,
            },
            google: {
                webClientId: socials.google.clientId,
                iOSClientId: socials.google.iOSClientId,
                mode: 'online',
            },
        });
    }

    private registerIncomingDeepLinkHandlers(): void {
        this.deepLinkService.onIncomingDeepLink('gameResult', (payload) => {
            void this.venueTargetArcadeService.handleGame(payload);
        });
    }

    /**
     * Ctrl + Alt + (T/Y) on Windows/Linux
     * Cmd + Option + (T/Y) on macOS
     * Smart devices: Ctrl + Alt + Shift + M on Windows/Linux, Cmd + Option + Shift + M on macOS
     */
    private readonly handleDevShortcuts = (event: KeyboardEvent): void => {
        if (!environment.debug) return;
        if (event.repeat || event.defaultPrevented) return;

        const code = event.code.toLowerCase();

        const ctrlKeys = (event.ctrlKey || event.metaKey) && event.altKey;
        if (!ctrlKeys) return;

        if (code === 'keyt') {
            event.preventDefault();
            this.modalService.openModal({
                component: ThemePreferenceDialogComponent,
                cssClass: slideDialogClasses,
                backdropDismiss: true,
            });
        } else if (code === 'keyy') {
            event.preventDefault();
            this.modalService.openModal({
                component: TextSizePreferenceDialogComponent,
                cssClass: slideDialogClasses,
                backdropDismiss: true,
            });
        } else if (code === 'keym') {
            event.preventDefault();
            void this.navController.navigateForward(toAbsoluteRoute(DC_ROUTING.SMART_DEVICES));
        }
    };
}
